import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { ServiceHour } from "../../api/types";
import useShowToast from "../../hooks/useShowToast";
import ConfirmModal from "../ui/confirm-modal";

type ServiceHourFormData = {
  name: string;
};

type SettingsServiceHoursModalProps = {
  isOpen: boolean;
  onClose: () => void;
  serviceHour?: ServiceHour;
};

const SettingsServiceHoursModal = ({ isOpen, onClose, serviceHour }: SettingsServiceHoursModalProps) => {
  const { t } = useTranslation();
  const { successToast, errorToast } = useShowToast();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ServiceHourFormData>();

  const {
    isOpen: isConfirmModalOpen,
    onOpen: onConfirmModalOpen,
    onClose: OnConfirmModalClose,
  } = useDisclosure();

  const onSubmit = async (data: ServiceHourFormData) => {
    setIsLoading(true);
    if (serviceHour?.id) {
      await api.updateServiceHour(serviceHour.id, { ...data });
    } else {
      await api.createServiceHour({ ...data });
    }

    if (api.error) {
      console.log("Error saving service hour", api.error);
      setIsLoading(false);
      errorToast({
        title: t("settings.service_hours.error_title"),
        description: t("settings.service_hours.error_message.default"),
      });
      return;
    }
    setIsLoading(false);
    onClose();

    successToast({
      title: t("settings.service_hours.success_title"),
      description: t("settings.service_hours.success_message"),
    });
  };

  const deleteServiceHour = async () => {
    if (!serviceHour?.id) {
      return;
    }

    setIsLoading(true);
    await api.deleteServiceHour(serviceHour.id);

    if (api.error) {
      console.log("Error deleting service hour", api.error);
      setIsLoading(false);
      errorToast({
        title: t("settings.service_hours.error_title"),
        description: t("settings.service_hours.error_message.default"),
      });
      return;
    }
    setIsLoading(false);
    onClose();

    successToast({
      title: t("settings.service_hours.modal.delete_success_title"),
      description: t("settings.service_hours.modal.delete_success_message"),
    });
  };

  useEffect(() => {
    reset({
      name: serviceHour?.name || "",
    });
  }, [serviceHour, reset]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent maxW="500px">
          {/* Modal title */}
          <ModalHeader>
            {serviceHour ? t("settings.service_hours.modal.edit_title") : t("settings.service_hours.modal.add_title")}
          </ModalHeader>

          <Divider />

          {/* Modal Body */}
          <ModalBody>
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={4}>
                <FormControl isRequired isInvalid={!!errors.name}>
                  <FormLabel>{t("settings.service_hours.modal.name")}</FormLabel>
                  <Input
                    {...register("name")}
                    placeholder={t("settings.service_hours.modal.name_placeholder")}
                    required
                  />
                </FormControl>
              </VStack>
            </form>
          </ModalBody>

          <Divider />

          {/* Modal Footer with buttons */}
          <ModalFooter>
            {serviceHour && (
              <Button colorScheme="red" onClick={onConfirmModalOpen} mr="auto">
                {t("settings.service_hours.modal.delete")}
              </Button>
            )}

            <Button variant="ghost" onClick={onClose}>
              {t("settings.service_hours.modal.cancel")}
            </Button>

            <Button
              colorScheme="blue"
              ml={3}
              onClick={handleSubmit(onSubmit)}
              isLoading={isLoading}
            >
              {serviceHour ? t("settings.service_hours.modal.edit") : t("settings.service_hours.modal.add")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={OnConfirmModalClose}
        onConfirm={() => deleteServiceHour()}
        colorScheme="red"
        message={t("settings.service_hours.modal.delete_message", { serviceHourName: serviceHour?.name })}
      />
    </>
  );
};

export default SettingsServiceHoursModal;
